/**
 * Starter buttons that depend on the page Emjae is open on.
 *
 * On a page with its own set, these replace the four generic mode buttons in the empty state, and
 * clicking one SENDS it (the label is the prompt: what the button says is what is asked). Every
 * other page returns [] and the caller keeps the generic buttons, which fill the box.
 *
 * The same table also gives the one-line welcome shown above the buttons (`welcomeFor`), so a
 * page's line and its buttons cannot disagree about which page this is.
 *
 * Pure: the table, the route match and the {client} wording live here and nowhere else.
 */

export interface ChatPagePreset {
  /** What the button says. */
  label: string;
  /** What is sent. The same text, on purpose. */
  prompt: string;
}

type PresetPage = 'clients' | 'brandBible' | 'audit' | 'content' | 'ads';

// `{client}` is replaced with the client's display name, or "this client".
const WORDING: Record<PresetPage, readonly string[]> = {
  clients: [
    'Add a new client',
    "What's the onboarding checklist?",
    'Who does which onboarding step?',
    "What's left to onboard {client}?",
  ],
  brandBible: [
    "What's missing in {client}'s brand bible?",
    'How do I fill the brand bible?',
    'Explain what each section is for',
    'Check the brand bible for problems',
  ],
  audit: [
    'What should I fix first?',
    "Summarise {client}'s audit",
    'Explain the top issue in plain words',
    'Which issues can Core fix for me?',
  ],
  content: [
    'Suggest the next articles for {client}',
    "What's scheduled this month?",
    'Which articles need attention?',
    'How do I publish an article?',
  ],
  ads: [
    "How is {client}'s ad spend pacing?",
    'What changed in the account this week?',
    'Which search terms are wasting money?',
    'Explain this page',
  ],
};

// Most specific prefix first; the first match wins. A prefix matches on a path-segment boundary
// (the path equals it or continues with "/"), so "/clientsx" is not "/clients".
const ROUTES: ReadonlyArray<{ prefix: string; page: PresetPage }> = [
  { prefix: '/clients/ads', page: 'ads' },
  { prefix: '/google-ads', page: 'ads' },
  { prefix: '/ads-tools', page: 'ads' },
  { prefix: '/clients', page: 'clients' },
  { prefix: '/brand-bible', page: 'brandBible' },
  { prefix: '/audit', page: 'audit' },
  { prefix: '/content', page: 'content' },
  { prefix: '/blog', page: 'content' },
];

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FALLBACK_CLIENT = 'this client';

/** The name that goes into a prompt: trimmed, never blank, never an id. */
function clientLabel(clientName: string | null | undefined): string {
  const name = typeof clientName === 'string' ? clientName.trim() : '';
  return name && !UUID_SHAPE.test(name) ? name : FALLBACK_CLIENT;
}

function pageFor(pathname: unknown): PresetPage | null {
  if (typeof pathname !== 'string' || !pathname.startsWith('/') || pathname.startsWith('//')) return null;
  const path = pathname.split(/[?#]/)[0];
  const hit = ROUTES.find(({ prefix }) => path === prefix || path.startsWith(`${prefix}/`));
  return hit ? hit.page : null;
}

/** A website the Offboard starter can name: trimmed, no whitespace, and not an id. */
function plainWebsite(value: string | null | undefined): string | null {
  const website = typeof value === 'string' ? value.trim() : '';
  return website && !/\s/.test(website) && !UUID_SHAPE.test(website) ? website : null;
}

/**
 * The page's starter buttons: at most four, plus on the Clients page a fifth, "Offboard <client>", when the
 * selected client's website is known (its prompt names the website, so what is sent is unambiguous). [] when
 * the page has none.
 */
export function presetsFor(
  pathname: string | undefined | null,
  clientName?: string | null,
  clientWebsite?: string | null,
): ChatPagePreset[] {
  const page = pageFor(pathname);
  if (!page) return [];
  const client = clientLabel(clientName);
  const presets = WORDING[page].slice(0, 4).map((template) => {
    // split/join, not replace(): a name such as "$&" or "{client}" is inserted literally.
    const text = template.split('{client}').join(client);
    return { label: text, prompt: text };
  });
  const website = page === 'clients' ? plainWebsite(clientWebsite) : null;
  if (website) presets.push({ label: `Offboard ${client}`, prompt: `Offboard ${website}` });
  return presets;
}

// One static line per page set; `{client}` as in WORDING.
const WELCOME: Record<PresetPage, string> = {
  clients: 'Hi! Onboarding a new client? I can walk you through every step, or answer questions about {client}.',
  brandBible: "Hi! I can help you fill and check {client}'s brand bible.",
  audit: "Hi! I can explain {client}'s audit and what to fix first.",
  content: "Hi! I can help plan and check {client}'s content.",
  ads: "Hi! I can explain how {client}'s ads are doing.",
};
const GENERIC_WELCOME = "Hi! I'm Emjae. Ask me anything about Core or this page.";

/** The empty-chat welcome line for the page: static text, never empty, the generic line off the table. */
export function welcomeFor(pathname: string | undefined | null, clientName?: string | null): string {
  const page = pageFor(pathname);
  if (!page) return GENERIC_WELCOME;
  return WELCOME[page].split('{client}').join(clientLabel(clientName));
}
